import { useEffect, useRef } from 'react';
import { useAppState, useTransitionDirection } from '../../context/AppContext.jsx';
import ErrorBoundary from '../common/ErrorBoundary.jsx';

/**
 * ScreenContainer — the host element for whichever screen is currently
 * selected. Owns three concerns:
 *
 *   1. Mounting / unmounting the active screen
 *      (we unmount inactive screens to free their scroll position + memory)
 *
 *   2. Replaying a slide-in animation on tab change
 *      The direction is derived from the global state — moving right in the
 *      tab order slides in from the right, moving left slides in from the
 *      left, mirroring native iOS/Android tab transitions.
 *
 *   3. Resetting scroll position when a screen mounts
 *      Mobile users expect re-entering a tab to land them at the top, NOT
 *      to restore the last scroll position. This is the inverse of the web
 *      router convention — intentional.
 *
 * The component takes a `screens` map ({ tabId: <ComponentInstance /> })
 * rather than rendering screen content itself so the routing decision lives
 * one level higher in App.jsx.
 */
/** DOM id of the screen area — see the overlay note in the render below. */
export const SCREEN_HOST_ID = 'screen-host';

export default function ScreenContainer({ screens }) {
  const { activeTab } = useAppState();
  const direction = useTransitionDirection();
  const scrollRef = useRef(null);

  // Reset scroll on tab change.
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [activeTab]);

  const ActiveScreen = screens[activeTab];

  return (
    // id + position:relative make this the mount point for screen-level
    // overlays (the news article reader portals here). Covering exactly the
    // screen area leaves the status bar and tab bar in place, so an overlay
    // never traps the user on one tab.
    <main
      id={SCREEN_HOST_ID}
      className="relative flex min-h-0 flex-1 flex-col overflow-hidden bg-bg-base"
    >
      <div
        // `key` forces a remount on tab change, replaying the entrance animation.
        key={activeTab}
        ref={scrollRef}
        className="scroll-rail relative flex-1 overflow-y-auto overscroll-contain animate-screen-in"
        data-direction={direction}
      >
        {/*
          Boundary lives inside the keyed wrapper, so it is thrown away and
          rebuilt on every tab change — switching tabs always clears a crashed
          screen rather than leaving it stuck in its error state.
        */}
        <ErrorBoundary label={activeTab}>{ActiveScreen}</ErrorBoundary>
      </div>
    </main>
  );
}
